import { useState, useCallback, useEffect } from 'react';
import { LabelTemplate, LabelElement, LABEL_WIDTH_MM, LABEL_HEIGHT_MM } from '@/types/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export interface Preset {
  id: string;
  name: string;
  description: string;
  icon: string;
  elements: LabelElement[];
  isActive: boolean;
  sortOrder: number;
  linkUrl: string | null;
  urlFields: string[] | null;
}

interface DbPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  elements: Json;
  is_active: boolean;
  sort_order: number;
  link_url: string | null;
  url_fields: string[] | null;
  created_at: string;
  updated_at: string;
}

const dbToPreset = (db: DbPreset): Preset => ({
  id: db.id,
  name: db.name,
  description: db.description,
  icon: db.icon,
  elements: db.elements as unknown as LabelElement[],
  isActive: db.is_active,
  sortOrder: db.sort_order,
  linkUrl: db.link_url,
  urlFields: db.url_fields,
});

export const usePresets = () => {
  const { user, isAdmin } = useAuth();
  const [presets, setPresets] = useState<Preset[]>([]);
  const [loading, setLoading] = useState(true);

  const loadPresets = useCallback(async () => {
    if (!user) {
      setPresets([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      // Admins can see all presets, regular users only see active ones
      let query = supabase
        .from('template_presets')
        .select('*')
        .order('sort_order', { ascending: true });

      // Non-admins should only request active presets (RLS also enforces this)
      if (!isAdmin) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading presets:', error);
        throw error;
      }

      const loadedPresets = (data || []).map((row) => dbToPreset(row as DbPreset));
      setPresets(loadedPresets);
    } catch (e) {
      console.error('Failed to load presets:', e);
      setPresets([]);
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin]);

  // Load presets and subscribe to realtime changes
  useEffect(() => {
    loadPresets();

    // Subscribe to realtime changes on template_presets
    const channel = supabase
      .channel('template_presets_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'template_presets',
        },
        () => {
          // Reload all presets when any change occurs
          loadPresets();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadPresets]);

  const createTemplate = useCallback((preset: Preset): LabelTemplate => ({
    id: `template-${Date.now()}`,
    name: preset.name,
    width: LABEL_WIDTH_MM,
    height: LABEL_HEIGHT_MM,
    elements: preset.elements.map(el => ({
      ...el,
      id: `${el.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    })),
    createdAt: Date.now(),
    updatedAt: Date.now(),
  }), []);

  const savePreset = useCallback(async (preset: Omit<Preset, 'id' | 'sortOrder'> & { id?: string }): Promise<Preset | null> => {
    if (!isAdmin) {
      toast.error('Only administrators can manage presets');
      return null;
    }

    try {
      if (preset.id) {
        // Update existing
        const { data, error } = await supabase
          .from('template_presets')
          .update({
            name: preset.name,
            description: preset.description,
            icon: preset.icon,
            elements: preset.elements as unknown as Json,
            is_active: preset.isActive,
          })
          .eq('id', preset.id)
          .select()
          .single();

        if (error) throw error;

        const updated = dbToPreset(data as DbPreset);
        setPresets(prev => prev.map(p => p.id === updated.id ? updated : p));
        toast.success('Preset updated');
        return updated;
      } else {
        // Create new
        const maxOrder = Math.max(0, ...presets.map(p => p.sortOrder));
        const { data, error } = await supabase
          .from('template_presets')
          .insert({
            name: preset.name,
            description: preset.description,
            icon: preset.icon,
            elements: preset.elements as unknown as Json,
            is_active: preset.isActive,
            sort_order: maxOrder + 1,
          })
          .select()
          .single();

        if (error) throw error;

        const created = dbToPreset(data as DbPreset);
        setPresets(prev => [...prev, created]);
        toast.success('Preset created');
        return created;
      }
    } catch (e) {
      console.error('Failed to save preset:', e);
      toast.error('Failed to save preset');
      return null;
    }
  }, [isAdmin, presets]);

  const deletePreset = useCallback(async (presetId: string) => {
    if (!isAdmin) {
      toast.error('Only administrators can manage presets');
      return;
    }

    try {
      const { error } = await supabase
        .from('template_presets')
        .delete()
        .eq('id', presetId);

      if (error) throw error;

      setPresets(prev => prev.filter(p => p.id !== presetId));
      toast.success('Preset deleted');
    } catch (e) {
      console.error('Failed to delete preset:', e);
      toast.error('Failed to delete preset');
    }
  }, [isAdmin]);

  const togglePresetActive = useCallback(async (presetId: string) => {
    if (!isAdmin) {
      toast.error('Only administrators can manage presets');
      return;
    }

    const preset = presets.find(p => p.id === presetId);
    if (!preset) return;

    try {
      const { error } = await supabase
        .from('template_presets')
        .update({ is_active: !preset.isActive })
        .eq('id', presetId);

      if (error) throw error;

      setPresets(prev => prev.map(p => 
        p.id === presetId ? { ...p, isActive: !p.isActive } : p
      ));
      toast.success(preset.isActive ? 'Preset hidden' : 'Preset visible');
    } catch (e) {
      console.error('Failed to toggle preset:', e);
      toast.error('Failed to update preset');
    }
  }, [isAdmin, presets]);

  // Get only active presets for regular users
  const activePresets = presets.filter(p => p.isActive);

  return {
    presets,
    activePresets,
    loading,
    createTemplate,
    savePreset,
    deletePreset,
    togglePresetActive,
  };
};
